import { useState, useEffect, useCallback, useRef } from 'react'
import { FileText, Printer, RefreshCw, Users, Clock, AlertTriangle, DollarSign, TrendingUp, Download } from 'lucide-react'
import { downloadPdf } from '../../utils/pdfUtils'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { sociosService } from '../../services/sociosService'
import toast from 'react-hot-toast'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—')

export default function RelatorioSocios() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const iframeRef = useRef(null)

  const [statusFiltro, setStatusFiltro] = useState('')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [loading, setLoading] = useState(false)
  const [dados, setDados] = useState(null)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [loadingXlsx, setLoadingXlsx] = useState(false)

  const carregar = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const params = {}
      if (statusFiltro) params.status = statusFiltro
      if (dataInicio) params.data_vencimento_inicio = dataInicio
      if (dataFim) params.data_vencimento_fim = dataFim
      const res = await sociosService.relatorio(orgId, params)
      setDados(res.data || res)
    } catch {
      toast.error('Erro ao carregar relatório')
    } finally {
      setLoading(false)
    }
  }, [orgId, statusFiltro, dataInicio, dataFim])

  useEffect(() => {
    if (orgId) carregar()
  }, [orgId]) // eslint-disable-line

  const handleImprimir = async () => {
    if (!orgId) return
    setLoadingPdf(true)
    try {
      const params = {}
      if (statusFiltro) params.status = statusFiltro
      if (dataInicio) params.data_vencimento_inicio = dataInicio
      if (dataFim) params.data_vencimento_fim = dataFim
      const res = await sociosService.relatorioPdf(orgId, params)
      const blob = new Blob([res.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(blob)
      setPdfUrl(url)
      setShowPdfModal(true)
    } catch {
      toast.error('Erro ao gerar PDF')
    } finally {
      setLoadingPdf(false)
    }
  }

  const handleExportarXlsx = async () => {
    if (!orgId) return
    setLoadingXlsx(true)
    try {
      const params = {}
      if (statusFiltro) params.status = statusFiltro
      if (dataInicio) params.data_vencimento_inicio = dataInicio
      if (dataFim) params.data_vencimento_fim = dataFim
      const res = await sociosService.exportarXlsx(orgId, params)
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'socios.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success('Planilha exportada com sucesso!')
    } catch {
      toast.error('Erro ao exportar planilha')
    } finally {
      setLoadingXlsx(false)
    }
  }

  const closePdfModal = () => { setShowPdfModal(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  const resumo = dados?.resumo || {}
  const ativos = dados?.ativos || []
  const canceladosVencidos = dados?.cancelados_vencidos || []

  const totalValorAtivos = ativos.reduce((s, c) => s + Number(c.valor || 0), 0)
  const totalValorCancelados = canceladosVencidos.reduce((s, c) => s + Number(c.valor || 0), 0)

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para gerar o relatório.
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="no-print flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-green-600" />
          <h1 className="text-lg font-semibold text-gray-800">Relatório de Sócios</h1>
        </div>
      </div>

      {/* Filtros */}
      <div className="no-print bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Status</label>
            <select value={statusFiltro} onChange={e => setStatusFiltro(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Todos</option>
              <option value="ativo">Ativo</option>
              <option value="pendente">Pendente</option>
              <option value="vencido">Vencido</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">Vencimento De</label>
              <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">Vencimento Até</label>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          <button onClick={carregar}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
            Filtrar
          </button>

          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleExportarXlsx} disabled={!dados || loadingXlsx}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              {loadingXlsx
                ? <RefreshCw size={15} className="animate-spin" />
                : <Download size={15} />}
              Exportar XLSX
            </button>
            <button onClick={handleImprimir} disabled={!dados}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> Imprimir
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        </div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Nenhum dado encontrado
        </div>
      ) : (
        <div className="print-page bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          {/* Cabeçalho impresso */}
          <div className="text-center mb-6 pb-4 border-b-2 border-gray-800">
            <p className="text-sm text-gray-600">{organizadorAtivo?.name}</p>
            <h2 className="text-xl font-black uppercase tracking-wide text-gray-900 mt-1">
              Relatório de Sócios
            </h2>
            <p className="text-xs text-gray-400 mt-1">
              Emitido em {new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })} às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })}
            </p>
          </div>

          {/* Cards resumo */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Users size={14} className="text-green-600" />
                <p className="text-xs text-green-600 font-medium">Sócios Ativos</p>
              </div>
              <p className="text-lg font-bold text-green-800">{resumo.total_ativos || 0}</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Clock size={14} className="text-yellow-600" />
                <p className="text-xs text-yellow-600 font-medium">Pendentes</p>
              </div>
              <p className="text-lg font-bold text-yellow-800">{resumo.total_pendentes || 0}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <AlertTriangle size={14} className="text-red-600" />
                <p className="text-xs text-red-600 font-medium">Vencidos</p>
              </div>
              <p className="text-lg font-bold text-red-800">{resumo.total_vencidos || 0}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <DollarSign size={14} className="text-blue-600" />
                <p className="text-xs text-blue-600 font-medium">Valor Recebido</p>
              </div>
              <p className="text-lg font-bold text-blue-800">{fmt(resumo.valor_recebido)}</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <TrendingUp size={14} className="text-purple-600" />
                <p className="text-xs text-purple-600 font-medium">A Receber</p>
              </div>
              <p className="text-lg font-bold text-purple-800">{fmt(resumo.valor_a_receber)}</p>
            </div>
          </div>

          {/* Seção SÓCIOS ATIVOS */}
          {ativos.length > 0 && (
            <div className="mb-8">
              <div className="bg-green-600 px-4 py-2 rounded-t-lg">
                <h3 className="text-sm font-bold text-white uppercase">
                  Sócios Ativos ({ativos.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border border-gray-200">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">Nome</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">CPF/CNPJ</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">Fazenda</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">Cidade/UF</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">Data Início</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">Vencimento</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">Valor</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">Data Pagamento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ativos.map((s, i) => (
                      <tr key={s.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 text-xs text-gray-700">{s.cliente_nome || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-700">{s.cliente_cpf_cnpj || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-700">{s.fazenda_nome || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-700">
                          {s.cliente_cidade ? `${s.cliente_cidade}/${s.cliente_estado || ''}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700">{fmtDate(s.data_inicio)}</td>
                        <td className="px-3 py-2 text-xs text-gray-700">{fmtDate(s.data_vencimento)}</td>
                        <td className="px-3 py-2 text-xs text-gray-800 font-semibold text-right">{fmt(s.valor)}</td>
                        <td className="px-3 py-2 text-xs text-gray-700">{fmtDate(s.data_pagamento)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-green-50 border-t border-gray-300">
                    <tr>
                      <td colSpan={6} className="px-3 py-2 text-xs font-bold text-gray-700 text-right">
                        Subtotal ({ativos.length} {ativos.length === 1 ? 'sócio' : 'sócios'}):
                      </td>
                      <td className="px-3 py-2 text-xs font-bold text-green-800 text-right">{fmt(totalValorAtivos)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Seção CANCELADOS / VENCIDOS */}
          {canceladosVencidos.length > 0 && (
            <div className="mb-8">
              <div className="bg-red-600 px-4 py-2 rounded-t-lg">
                <h3 className="text-sm font-bold text-white uppercase">
                  Sócios Cancelados / Vencidos ({canceladosVencidos.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border border-gray-200">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">Nome</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">CPF/CNPJ</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">Fazenda</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">Cidade/UF</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">Status</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600">Data Cancel./Venc.</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {canceladosVencidos.map((s, i) => (
                      <tr key={s.id} className={i % 2 === 0 ? 'bg-white' : 'bg-red-50/30'}>
                        <td className="px-3 py-2 text-xs text-gray-700">{s.cliente_nome || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-700">{s.cliente_cpf_cnpj || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-700">{s.fazenda_nome || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-700">
                          {s.cliente_cidade ? `${s.cliente_cidade}/${s.cliente_estado || ''}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            s.status === 'cancelado'
                              ? 'bg-gray-100 text-gray-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {s.status === 'cancelado' ? 'Cancelado' : 'Vencido'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700">
                          {s.status === 'cancelado' ? fmtDate(s.updated_at?.split('T')[0]) : fmtDate(s.data_vencimento)}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-800 font-semibold text-right">{fmt(s.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-red-50 border-t border-gray-300">
                    <tr>
                      <td colSpan={6} className="px-3 py-2 text-xs font-bold text-gray-700 text-right">
                        Subtotal ({canceladosVencidos.length} {canceladosVencidos.length === 1 ? 'sócio' : 'sócios'}):
                      </td>
                      <td className="px-3 py-2 text-xs font-bold text-red-800 text-right">{fmt(totalValorCancelados)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Caso nenhuma seção tenha dados */}
          {ativos.length === 0 && canceladosVencidos.length === 0 && (
            <div className="text-center text-gray-400 text-sm py-8">
              Nenhum sócio encontrado com os filtros selecionados.
            </div>
          )}
        </div>
      )}

      {/* Modal PDF iframe */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Relatório de Sócios</span>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(pdfUrl, 'Relatorio_Socios.pdf')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Salvar PDF
                </button>
                <button onClick={printPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button onClick={closePdfModal}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Relatório de Sócios"
                style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
